import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { CAM_FIELDS, CAM_SELECTS } from "@/lib/vvMaint";
import { SlotPhotos, useEvidence } from "@/components/wo/SlotPhotos";
import { SurveyImport } from "@/components/wo/SurveyImport";

const uid = () => Math.random().toString(36).slice(2, 10);
const Head = ({ children }) => <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">{children}</div>;
const sel = "h-10 w-full rounded-md border border-border bg-transparent px-2 text-sm";

const CamCard = ({ c, i, set, remove, ed, photos }) => (
  <div className="space-y-2 rounded-lg border border-border p-3" data-testid={`vv-cam-${i}`}>
    <div className="flex items-center text-sm font-medium">Cámara {c.numero || i + 1}{ed && <Button size="icon" variant="ghost" className="ml-auto" onClick={remove} data-testid={`vv-cam-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>}</div>
    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
      {CAM_FIELDS.map(([f, l]) => <div key={f} className="space-y-1"><Label className="text-[11px] text-muted-foreground">{l}</Label><Input className="h-10" value={c[f] || ""} disabled={!ed} onChange={(e) => set({ ...c, [f]: e.target.value })} data-testid={`vv-cam-${i}-${f}`} /></div>)}
      {CAM_SELECTS.map(([f, l, opts]) => <div key={f} className="space-y-1"><Label className="text-[11px] text-muted-foreground">{l}</Label>
        <select className={sel} value={c[f] || ""} disabled={!ed} onChange={(e) => set({ ...c, [f]: e.target.value })} data-testid={`vv-cam-${i}-${f}`}><option value="">Seleccionar</option>{opts.map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select></div>)}
    </div>
    <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!c.intervencion} disabled={!ed} onCheckedChange={(v) => set({ ...c, intervencion: !!v })} data-testid={`vv-cam-${i}-intervencion`} />Hubo intervención (requiere foto DESPUÉS)</label>
    {photos ? <div className="grid gap-2 sm:grid-cols-2">
      <SlotPhotos {...photos} slot={`vvcam:${c.id}:antes`} label="Foto ANTES" required testid={`vv-cam-${i}-antes`} />
      <SlotPhotos {...photos} slot={`vvcam:${c.id}:despues`} label="Foto DESPUÉS" required={!!c.intervencion} testid={`vv-cam-${i}-despues`} />
    </div> : <p className="text-xs text-muted-foreground">Guarda la cámara para agregar sus fotos ANTES / DESPUÉS.</p>}
  </div>
);

const Note = ({ k, title, value, onChange, ed, photos }) => (
  <div className="space-y-2" data-testid={`vv-note-${k}`}>
    <Head>{title}</Head>
    <Textarea rows={3} value={value || ""} disabled={!ed} onChange={(e) => onChange(e.target.value)} data-testid={`vv-note-${k}-text`} />
    <SlotPhotos {...photos} slot={`vvnote:${k}`} label="Fotografías" testid={`vv-note-${k}-photos`} />
  </div>
);

export const VvMaintenance = ({ wo, setWo, editable }) => {
  const [cams, setCams] = useState(wo.tech_data?.vv_cameras || []);
  const [rep, setRep] = useState(wo.tech_data?.vv_report || {});
  const [busy, setBusy] = useState(false);
  const [ev, reload] = useEvidence(wo.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCams(wo.tech_data?.vv_cameras || []); setRep(wo.tech_data?.vv_report || {}); }, [wo.id, wo.tech_data?.vv_import?.at]);
  const savedIds = new Set((wo.tech_data?.vv_cameras || []).map((c) => c.id));
  const photos = { woId: wo.id, items: ev, reload, editable: editable || wo.status === "pendiente_cierre" };
  const onTd = (td) => setWo((w) => ({ ...w, tech_data: td }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/work-orders/${wo.id}/tech-data`, { vv_cameras: cams, vv_report: rep }); onTd(data); toast.success("Cámaras, hallazgos y recomendaciones guardados"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="vv-maintenance">
      {editable && <SurveyImport wo={wo} onImported={onTd} />}
      <div className="space-y-2" data-testid="vv-cameras">
        <Head>Cámaras y puntos de vigilancia ({cams.length})</Head>
        {cams.map((c, i) => <CamCard key={c.id} c={c} i={i} ed={editable} photos={savedIds.has(c.id) ? photos : null}
          set={(nc) => setCams(cams.map((x, j) => (j === i ? nc : x)))} remove={() => setCams(cams.filter((_, j) => j !== i))} />)}
        {!cams.length && <p className="text-sm text-muted-foreground">Sin cámaras registradas.</p>}
        {editable && <Button size="sm" variant="outline" className="w-full" onClick={() => setCams([...cams, { id: uid(), numero: String(cams.length + 1) }])} data-testid="vv-cam-add"><Plus className="mr-1 h-4 w-4" />Agregar cámara</Button>}
      </div>
      <Note k="hallazgos" title="A. Hallazgos" value={rep.hallazgos} onChange={(v) => setRep({ ...rep, hallazgos: v })} ed={editable} photos={photos} />
      <Note k="recomendaciones" title="B. Recomendaciones" value={rep.recomendaciones} onChange={(v) => setRep({ ...rep, recomendaciones: v })} ed={editable} photos={photos} />
      {editable && <Button className="h-12 w-full" onClick={save} disabled={busy} data-testid="vv-maintenance-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar cámaras, hallazgos y recomendaciones</Button>}
    </div>
  );
};
